import { useEffect, useState } from "react";
import { Bell } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from "@/components/ui/dropdown-menu";
import { formatDistanceToNow } from "date-fns";

export default function NotificationBell() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const loadCount = async () => {
    if (!user || user === false) return;
    try {
      const { data } = await api.get("/notifications/unread-count");
      setCount(data.count);
    } catch {}
  };

  useEffect(() => {
    loadCount();
    if (!user || user === false) return;
    const t = setInterval(loadCount, 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const onOpenChange = async (v) => {
    setOpen(v);
    if (v) {
      const { data } = await api.get("/notifications");
      setItems(data);
      await api.post("/notifications/mark-read");
      setCount(0);
    }
  };

  if (!user || user === false) return null;

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="notification-bell"
          className="relative p-2 rounded-full hover:bg-[#F9EFE6] transition"
          aria-label="Notifications"
        >
          <Bell className="w-5 h-5 text-[#3B2C24]" />
          {count > 0 && (
            <span
              data-testid="notification-count"
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF7E67] text-white text-[10px] font-semibold grid place-items-center"
            >
              {count > 9 ? "9+" : count}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-[420px] overflow-y-auto rounded-2xl border-[#EBDCD1]">
        <div className="px-3 py-2 border-b border-[#EBDCD1] font-semibold text-sm text-[#3B2C24]">
          Notifications
        </div>
        {items.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-[#857163]">
            No notifications yet.
          </div>
        ) : (
          items.map((n) => (
            <button
              key={n.id}
              onClick={() => {
                setOpen(false);
                if (n.type === "rules-reminder") nav("/rules");
                else if (n.type === "message") nav(`/messages/${n.actor_id || ""}`);
                else if (n.type === "broadcast") {
                  // System broadcasts have no target — expanding the row is enough.
                }
                else if (n.type === "low_stock") nav("/seller-dashboard");
                else if (n.post_id) nav(`/p/${n.post_id}`);
              }}
              className={`w-full text-left px-3 py-3 border-b border-[#EBDCD1]/50 hover:bg-[#F9EFE6]/60 transition ${n.type === "rules-reminder" ? "bg-red-50/40" : ""}`}
              data-testid={`notification-item-${n.id}`}
            >
              {n.type === "rules-reminder" ? (
                <>
                  <div className="text-sm text-[#3B2C24] font-semibold flex items-center gap-1.5">
                    <Bell className="w-3.5 h-3.5 text-red-500" /> Rules reminder
                  </div>
                  <div className="text-xs text-[#3B2C24] mt-1">
                    {n.message || `Your post "${n.post_title}" was reported for ${n.reason_label}.`}
                  </div>
                  <div className="text-[10px] text-[#FF7E67] mt-1 font-medium">Tap to review House Rules →</div>
                </>
              ) : n.type === "message" ? (
                <div className="text-sm text-[#3B2C24]">
                  <span className="font-semibold">@{n.actor_username}</span> sent you a message
                </div>
              ) : n.type === "broadcast" ? (
                <>
                  <div className="text-sm text-[#3B2C24] font-semibold flex items-center gap-1.5">
                    <Bell className="w-3.5 h-3.5 text-[#FF7E67]" /> {n.subject || `Update from @${n.actor_username}`}
                  </div>
                  <div className="text-xs text-[#3B2C24] mt-1 line-clamp-3 whitespace-pre-wrap">{n.message}</div>
                </>
              ) : n.type === "low_stock" ? (
                <>
                  <div className="text-sm text-[#3B2C24] font-semibold flex items-center gap-1.5">
                    <Bell className="w-3.5 h-3.5 text-[#F4A83B]" /> {n.subject || "Low stock alert"}
                  </div>
                  <div className="text-xs text-[#3B2C24] mt-1">{n.message}</div>
                  <div className="text-[10px] text-[#FF7E67] mt-1 font-medium">Tap to update stock →</div>
                </>
              ) : (
                <div className="text-sm text-[#3B2C24]">
                  <span className="font-semibold">@{n.actor_username}</span>{" "}
                  {n.type === "reply" ? "replied to your comment on" : "commented on"}{" "}
                  <span className="font-medium">"{n.post_title}"</span>
                </div>
              )}
              <div className="text-xs text-[#857163] mt-0.5">
                {(() => {
                  try { return formatDistanceToNow(new Date(n.created_at), { addSuffix: true }); }
                  catch { return ""; }
                })()}
              </div>
            </button>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
